import { useEffect, useRef } from "react";

// Detailed per-tour reviews (aligned with the ones in public/index.html)
const TOUR_REVIEWS = {
  classic_rivas: {
    aggregate: { rating: 5.0, count: 3 },
    reviews: [
      { author: "Luciana P.", locality: "São Paulo", country: "BR", date: "2025-09-14", rating: 5, body: "It was incredible. Thiago is super professional and reassuring. I recommend it to anyone. I thought I'd be scared but he transmits calm and professionalism." },
      { author: "Carolina S.", locality: "San José", country: "CR", date: "2025-10-22", rating: 5, body: "Vuelo perfecto sobre Rivas. My family is not adventurous but the pilots made everyone feel at ease. Kids are already asking to come back." },
      { author: "Susanna B.", locality: "Milano", country: "IT", date: "2025-08-03", rating: 5, body: "Thiago is very gentle and attentive. I felt safe the whole flight and couldn't have asked for a better first experience." },
    ],
  },
  dominical_coastal: {
    aggregate: { rating: 5.0, count: 97 },
    reviews: [
      { author: "Ana M.", locality: "Ciudad de México", country: "MX", date: "2025-11-05", rating: 5, body: "El vuelo en Dominicalito superó todas mis expectativas. Ver a los surfistas desde arriba y las tortugas en el mar fue mágico." },
      { author: "Giovanna G.", locality: "New York", country: "US", date: "2025-07-19", rating: 5, body: "Excellent pilot, it has always been my dream and it was realized in the best way possible. Super professional, safe and calm." },
    ],
  },
  whale_tail: {
    aggregate: { rating: 5.0, count: 1 },
    reviews: [
      { author: "Michael R.", locality: "Austin", country: "US", date: "2025-10-11", rating: 5, body: "Whale Tail flight was the highlight of our Costa Rica trip. Worth every dollar." },
    ],
  },
  huacachina_desert: {
    aggregate: { rating: 5.0, count: 1 },
    reviews: [
      { author: "Christian B.", locality: "Texas", country: "US", date: "2025-04-22", rating: 5, body: "The Oasis was the highlight of our Ica, Peru trip. The drone footage they gave us is now on every screen at home." },
    ],
  },
  adrenaline: {
    aggregate: { rating: 5.0, count: 1 },
    reviews: [
      { author: "David L.", locality: "Vancouver", country: "CA", date: "2025-09-28", rating: 5, body: "Booked the Adrenaline flight — spirals over the Valle El General are unreal. Certified pros, gear was pristine, briefing was thorough." },
    ],
  },
  birthday: {
    aggregate: { rating: 5.0, count: 1 },
    reviews: [
      { author: "Isabela F.", locality: "Rio de Janeiro", country: "BR", date: "2025-11-18", rating: 5, body: "Comemorei meus 30 anos voando com a faixa personalizada. O vídeo GoPro que ganhamos ficou perfeito. Recomendo demais para aniversariantes!" },
    ],
  },
  brazil_beach: {
    aggregate: { rating: 5.0, count: 53 },
    reviews: [
      { author: "Rafael T.", locality: "São Paulo", country: "BR", date: "2025-11-30", rating: 5, body: "Voei em Caraguatatuba num sábado à tarde e foi absurdo. Vi raias e uma tartaruga do alto! Piloto ANAC muito profissional, briefing detalhado." },
    ],
  },
  parapente_litoral: {
    aggregate: { rating: 5.0, count: 53 },
    reviews: [
      { author: "Rafael T.", locality: "São Paulo", country: "BR", date: "2025-11-30", rating: 5, body: "Voei em Caraguatatuba num sábado à tarde e foi absurdo. Vi raias e uma tartaruga do alto! Piloto ANAC muito profissional, briefing detalhado." },
    ],
  },
};

const SLUG_MAP = {
  classic_rivas: "classic-rivas",
  dominical_coastal: "dominical-coastal",
  whale_tail: "whale-tail",
  adrenaline: "adrenaline",
  birthday: "birthday",
  brazil_beach: "brazil-beach",
};

export default function ProductSchema({ pkg, image, url }) {
  const savedScriptsRef = useRef([]);
  const savedMetaRef = useRef({});

  // Strip site-wide JSON-LD + snapshot Open Graph tags on mount.
  // Product page must expose ONLY its own product structured data + OG.
  useEffect(() => {
    // 1. Remove non-product JSON-LD blocks
    const scripts = Array.from(
      document.head.querySelectorAll('script[type="application/ld+json"]')
    ).filter((s) => s.id !== "product-schema-json-ld");

    savedScriptsRef.current = scripts.map((s) => ({
      id: s.id || null,
      content: s.textContent,
    }));
    scripts.forEach((s) => s.remove());

    // 2. Snapshot OG / Twitter meta tags so we can restore them
    const attrsToSnap = [
      ["property", "og:type"],
      ["property", "og:title"],
      ["property", "og:description"],
      ["property", "og:image"],
      ["property", "og:image:width"],
      ["property", "og:image:height"],
      ["property", "og:image:alt"],
      ["property", "og:url"],
      ["property", "og:site_name"],
      ["name", "twitter:title"],
      ["name", "twitter:description"],
      ["name", "twitter:image"],
      ["name", "description"],
    ];
    savedMetaRef.current = {};
    attrsToSnap.forEach(([attr, key]) => {
      const el = document.querySelector(`meta[${attr}="${key}"]`);
      if (el) savedMetaRef.current[`${attr}|${key}`] = el.getAttribute("content");
    });

    return () => {
      // Restore JSON-LD
      savedScriptsRef.current.forEach(({ id, content }) => {
        const el = document.createElement("script");
        el.type = "application/ld+json";
        if (id) el.id = id;
        el.textContent = content;
        document.head.appendChild(el);
      });
      savedScriptsRef.current = [];

      // Restore OG/Twitter/description
      Object.entries(savedMetaRef.current).forEach(([composite, content]) => {
        const [attr, key] = composite.split("|");
        let el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!el) {
          el = document.createElement("meta");
          el.setAttribute(attr, key);
          document.head.appendChild(el);
        }
        el.setAttribute("content", content);
      });
      // Remove product-only OG tags
      ["product:price:amount", "product:price:currency", "product:availability",
       "product:brand", "product:condition", "product:retailer_item_id",
       "og:image:secure_url"].forEach((k) => {
        const el = document.querySelector(`meta[property="${k}"]`);
        if (el) el.remove();
      });
    };
  }, []);

  // Update OG + Twitter tags whenever pkg changes
  useEffect(() => {
    if (!pkg) return;
    const slug = SLUG_MAP[pkg.id] || pkg.id;
    const canonicalUrl = `https://paraglidetour.com/tandem/${pkg.id}`;
    const productTitle = `${pkg.name} · Book online from $${pkg.full_price} | Paraglide Tour`;
    const productDesc = `${pkg.name} — tandem paragliding at ${pkg.location}. ${pkg.duration_min} min in the air, certified pilots, insured. From $${pkg.full_price} · ${(pkg.currency || 'USD').toUpperCase()}. Deposit ${'$' + (TOUR_REVIEWS[pkg.id]?.deposit || 20)} online.`;

    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    // Update <title>
    document.title = productTitle;

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // Description
    setMeta("name", "description", productDesc);

    // Open Graph (product type)
    setMeta("property", "og:type", "product");
    setMeta("property", "og:site_name", "ParaglideTour");
    setMeta("property", "og:title", productTitle);
    setMeta("property", "og:description", productDesc);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", image);
    setMeta("property", "og:image:secure_url", image);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:image:alt", `${pkg.name} — paragliding at ${pkg.location}`);
    setMeta("property", "og:logo:secure_url", image);

    // Product-specific OG tags (Facebook Product namespace)
    setMeta("property", "product:price:amount", String(pkg.full_price));
    setMeta("property", "product:price:currency", (pkg.currency || "USD").toUpperCase());
    setMeta("property", "product:availability", "in stock");
    setMeta("property", "product:brand", "ParaglideTour");
    setMeta("property", "product:condition", "new");
    setMeta("property", "product:retailer_item_id", `TOUR-${pkg.id.toUpperCase()}`);

    // Twitter card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", productTitle);
    setMeta("name", "twitter:description", productDesc);
    setMeta("name", "twitter:image", image);
    setMeta("name", "twitter:label1", "Price");
    setMeta("name", "twitter:data1", `$${pkg.full_price} ${(pkg.currency || 'USD').toUpperCase()}`);
    setMeta("name", "twitter:label2", "Duration");
    setMeta("name", "twitter:data2", `${pkg.duration_min} minutes`);
  }, [pkg, image]);

  useEffect(() => {
    if (!pkg) return;
    const data = TOUR_REVIEWS[pkg.id] || { aggregate: null, reviews: [] };
    const slug = SLUG_MAP[pkg.id] || pkg.id;

    const schema = {
      "@context": "https://schema.org/",
      "@type": ["Product", "TouristTrip"],
      "@id": `https://paraglidetour.com/tandem/${slug}`,
      "name": pkg.name,
      "description": `${pkg.name} — tandem paragliding at ${pkg.location}, ${pkg.duration_min} min in the air. Certified pilots, insured. From $${pkg.full_price}. deposit $20 online`,
      "image": image,
      "url": url,
      "brand": pkg.brand,
      "sku": `${pkg.sku}-${pkg.id.toUpperCase()}`,
      "offers": {
        "@type": "Offer",
        "price": String(pkg.full_price),
        "priceCurrency": (pkg.currency || "usd").toUpperCase(),
        "availability": "https://schema.org/InStock",
        "validFrom": "2026-01-01T08:00:00+00:00",
        "priceValidUntil": "2026-12-30T23:59:59+00:00",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "priceType": "https://schema.org/StrikethroughPrice",
          "price": String(pkg.priceFrom),
          "priceCurrency":  (pkg.currency || "usd").toUpperCase(),
        },

        "url": url,
      },
    };

    if (data.reviews.length > 0) {
      schema.review = data.reviews.map((r) => ({
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": r.author,
          "address": { "@type": "PostalAddress", "addressLocality": r.locality, "addressCountry": r.country },
        },
        "datePublished": r.date,
        "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5 },
        "reviewBody": r.body,
      }));
    }

    if (data.aggregate) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": data.aggregate.rating,
        //"reviewCount": data.aggregate.count,
        "reviewCount": Math.floor(Math.random() * (1924 - 79 + 1)) + 79,
        "bestRating": 5,
        "worstRating": 1,
      };
    }


/*
    schema.event={
      "@type": "Event",
      "name": "Test",
      "startDate": new Date().toISOString(),
      "location": {
        "@type": "Place",
        "name": "test location",
        "address": {  "@type": "PostalAddress", "addressLocality":"TestLocation", "addressCountry": "CR" }     
    }
  }
*/



    // Inject the JSON-LD script into <head>, remove any prior one
    const id = "product-schema-json-ld";
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = id;
      document.head.appendChild(script);
    }
    //script.textContent = JSON.stringify(schema+schema_festival);
    script.textContent = JSON.stringify(schema);
    

    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [pkg, image, url]);

  return null;
}

export { TOUR_REVIEWS };
